import { ref } from 'vue'
import { getAuth } from 'firebase/auth'
import { collection, getDocs, getDoc, query, updateDoc, doc, where, writeBatch, serverTimestamp, FieldPath, deleteField } from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { isInternalEmail } from '@/utils/emailDomains'
import {
  asCompanyRoles,
  clientTypeForRole,
  customRoleIdFor,
  resolveCompanyRole,
  resolveUserRole,
  type CompanyPermissions,
  type CompanyRole,
  type CompanyRoleEntry,
  type CompanyRoles,
  type ModulePermissions,
  type UserRole
} from '@/domain/types/Role'
import { buildRoleActor, type AssignableRole } from '@/domain/types/CustomRole'

export interface CompanyUser {
  uid: string
  name: string
  lastName: string
  email: string
  role: UserRole
  status: string
  lastLoginAt: Date | null
  /** Permisos del usuario en TODAS sus compañías; se conservan al guardar. */
  allPermissions: CompanyPermissions
  /** Permisos en la compañía activa. undefined = sin configurar, acceso completo. */
  permissions: ModulePermissions | undefined
  /** companyRoles en TODAS sus compañías; se conserva al guardar. */
  allCompanyRoles: CompanyRoles
  /** Ids de compañías del usuario (para decidir si se refleja el rol global, D4). */
  companyIds: string[]
  /** Rol personalizado en la compañía activa (solo si el rol efectivo es 'regular'). */
  customRoleId: string | null
}

const toDate = (value: any): Date | null => {
  if (!value) return null
  if (typeof value.toDate === 'function') return value.toDate()
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Los usuarios antiguos tenían permissions como array; se ignora. */
const asPermissionsMap = (value: any): CompanyPermissions =>
  value && !Array.isArray(value) && typeof value === 'object' ? value : {}

export function useCompanyUsers() {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  const db = FirebaseProvider.getInstance().getFirestore()

  const users = ref<CompanyUser[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const currentCompanyId = () => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No hay compañía seleccionada')
    return companyId
  }

  const fetchUsers = async () => {
    try {
      isLoading.value = true
      error.value = null

      const companyId = currentCompanyId()
      const snapshot = await getDocs(
        query(collection(db, 'Users'), where('companyIds', 'array-contains', companyId))
      )

      // El equipo de la plataforma no aparece en el listado de una compañía cliente;
      // solo se ven entre ellos.
      const viewerIsInternal = isInternalEmail(authStore.user?.email || '')

      users.value = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data()
          const allPermissions = asPermissionsMap(data.permissions)
          const allCompanyRoles = asCompanyRoles(data.companyRoles)
          const globalRole = resolveUserRole(data.role, data.clientType)
          const role = resolveCompanyRole(globalRole, allCompanyRoles, companyId) || 'regular'

          return {
            uid: docSnap.id,
            name: data.name || '',
            lastName: data.lastName || '',
            email: data.email || '',
            role,
            status: data.status || 'active',
            lastLoginAt: toDate(data.lastLoginAt),
            allPermissions,
            permissions: allPermissions[companyId],
            allCompanyRoles,
            companyIds: Array.isArray(data.companyIds)
              ? data.companyIds
              : (data.companiesRef || []).map((c: any) => c.id),
            customRoleId: customRoleIdFor(allCompanyRoles, companyId, role)
          }
        })
        .filter((user) => viewerIsInternal || !isInternalEmail(user.email))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
    } catch (err: any) {
      error.value = err.message || 'Error cargando los usuarios'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const adminCount = () => users.value.filter((u) => u.role === 'admin').length

  /**
   * Asigna un rol (predefinido o personalizado) en la compañía activa. Solo el
   * admin; nadie puede cambiarse el rol a sí mismo ni dejar la compañía sin admins.
   */
  const changeRole = async (
    user: CompanyUser,
    target: AssignableRole,
    previous: AssignableRole
  ): Promise<void> => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede cambiar roles')
    }
    if (user.uid === authStore.user?.uid) {
      throw new Error('No puedes cambiar tu propio rol')
    }
    if (user.role === 'superadmin') {
      throw new Error('No se puede cambiar el rol de un superadmin')
    }
    if (user.role === 'admin' && target.id !== 'admin' && adminCount() <= 1) {
      throw new Error('Es el único administrador de la compañía. Asigna otro antes de cambiarle el rol.')
    }

    const cid = currentCompanyId()
    const baseRole: CompanyRole = target.isPredefined ? (target.id as CompanyRole) : 'regular'
    // Sin claves undefined: Firestore las rechaza.
    const entry: CompanyRoleEntry = target.isPredefined
      ? { role: baseRole }
      : { role: 'regular', customRoleId: target.id }
    const companyRoles = { ...user.allCompanyRoles, [cid]: entry }
    const permissions = { ...user.allPermissions }
    delete permissions[cid]

    // El listado puede estar desactualizado: la pertenencia a una sola compañía
    // se decide con el documento fresco. Se escribe solo la llave de la compañía
    // (FieldPath, por si el id trae puntos) para no pisar lo que otro admin
    // haya cambiado en las demás.
    const fresh = await getDoc(doc(db, 'Users', user.uid))
    // Mismo conjunto que cuenta la regla: companyIds ∪ {companyId}.
    const freshData = fresh.exists() ? fresh.data() : undefined
    const freshIds = new Set<string>(
      freshData && Array.isArray(freshData.companyIds) ? freshData.companyIds : user.companyIds
    )
    if (freshData?.companyId) freshIds.add(freshData.companyId)
    const mirrorGlobal = freshIds.size <= 1

    const batch = writeBatch(db)
    batch.update(
      doc(db, 'Users', user.uid),
      new FieldPath('companyRoles', cid), entry,
      new FieldPath('permissions', cid), deleteField(),
      'updatedAt', new Date(),
      ...(mirrorGlobal ? ['role', baseRole, 'clientType', clientTypeForRole(baseRole)] : [])
    )
    batch.set(doc(collection(db, 'Companies', cid, 'roleHistory')), {
      type: 'user_role_changed',
      roleId: target.id,
      roleName: target.name,
      targetUser: { uid: user.uid, email: user.email },
      before: {
        roleId: previous.id,
        roleName: previous.name,
        hadIndividualPermissions: !!user.permissions
      },
      after: { roleId: target.id, roleName: target.name },
      actor: buildRoleActor(authStore.user, getAuth().currentUser?.uid),
      at: serverTimestamp()
    })
    await batch.commit()

    user.role = baseRole
    user.allCompanyRoles = companyRoles
    user.customRoleId = target.isPredefined ? null : target.id
    user.allPermissions = permissions
    user.permissions = undefined
  }

  /**
   * Guarda los permisos del usuario en la compañía activa. Si conserva todas las
   * acciones de todos los módulos disponibles, se borra el mapa: la ausencia
   * significa acceso completo, y así los módulos que se habiliten más adelante
   * quedan accesibles sin tener que reeditar a cada usuario.
   */
  const savePermissions = async (
    user: CompanyUser,
    permissions: ModulePermissions,
    hasEverything: boolean
  ) => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede cambiar permisos')
    }
    const companyId = currentCompanyId()
    const allPermissions = { ...user.allPermissions }

    if (hasEverything) delete allPermissions[companyId]
    else allPermissions[companyId] = permissions

    await updateDoc(doc(db, 'Users', user.uid), {
      permissions: allPermissions,
      updatedAt: new Date()
    })

    user.allPermissions = allPermissions
    user.permissions = allPermissions[companyId]
  }

  /**
   * Elimina al usuario a través de la Cloud Function: si pertenece a otras
   * compañías solo se desvincula de esta, y si era la última se borra también su
   * cuenta de Firebase Auth. Borrarlo desde el cliente no es posible.
   */
  const removeUser = async (user: CompanyUser) => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede eliminar usuarios')
    }
    if (user.uid === authStore.user?.uid) {
      throw new Error('No puedes eliminarte a ti mismo')
    }
    if (user.role === 'admin' && adminCount() <= 1) {
      throw new Error('Es el único administrador de la compañía. Asigna otro antes de eliminarlo.')
    }

    const currentUser = getAuth().currentUser
    if (!currentUser) throw new Error('Sesión no válida')

    const token = await currentUser.getIdToken()
    const response = await fetch(`${import.meta.env.VITE_CF_BASE_URL}/DELETE_COMPANY_USER`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ company: currentCompanyId(), uid: user.uid })
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'No se pudo eliminar el usuario')
    }

    users.value = users.value.filter((u) => u.uid !== user.uid)
    return result.action as 'deleted' | 'unlinked'
  }

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    changeRole,
    savePermissions,
    removeUser
  }
}
